import React, { useEffect, useMemo, useRef, useState } from 'react';
import productService from '../../services/api/productService';

interface Props {
  productId: number;
  onComplete: () => void;
  onSkip: () => void;
}

const CONTAINER =
  'grid grid-cols-[minmax(240px,1fr)_minmax(240px,1fr)] gap-7 items-center max-[720px]:grid-cols-[1fr]';
const PANEL = 'p-5 border border-gray-200 rounded-[14px] bg-[#fafafa]';
const SPINNER =
  'w-[54px] h-[54px] border-4 border-gray-200 border-t-primary rounded-full animate-spin mx-auto mt-0 mb-[18px]';
const TITLE = 'text-center text-gray-800 text-[1.35rem] m-0 mb-2';
const COPY = 'text-center text-gray-500 text-[0.88rem] leading-[1.7] m-0';
const PROGRESS =
  'h-[7px] bg-gray-200 rounded-[999px] overflow-hidden mx-0 mt-5 mb-3';
const FILL =
  'h-full bg-gradient-to-r from-[#616fc9] to-violet-500 transition-[width] duration-[400ms] ease-[ease]';
const VIDEO =
  'aspect-video rounded-xl bg-gradient-to-br from-[#20243f] to-[#616fc9] grid place-items-center text-white shadow-[0_12px_30px_rgba(44,50,100,0.18)]';
const PLAY =
  'w-[58px] h-[58px] rounded-full grid place-items-center bg-white/[0.18] border border-white/[0.45] text-[1.3rem]';
const VIDEO_LABEL = 'font-bold text-[#303653] mx-0 mt-3 mb-1';
const VIDEO_NOTE = 'text-[0.78rem] text-[#8a8fa3] m-0';
const SKIP =
  'block mx-auto mt-[18px] mb-0 border-0 bg-transparent bg-none text-[#8a8fa3] underline cursor-pointer';

const SignupResearchStep: React.FC<Props> = ({
  productId,
  onComplete,
  onSkip,
}) => {
  const [progress, setProgress] = useState(10);
  const completedRef = useRef(false);
  const labels = useMemo(
    () => ['商品情報', 'ブランド情報', '画像解析', '競合情報'],
    [],
  );
  useEffect(() => {
    let cancelled = false;
    productService.startProductResearch(productId).catch(() => undefined);
    const poll = async () => {
      try {
        const data = await productService.getKnowledgeFetchProgress(productId);
        if (cancelled) return;
        const values = Object.values(data.fetch_progress || {});
        const done = values.filter(
          (v) => v === 'completed' || v === 'completed_with_warning' || v === 'failed',
        ).length;
        setProgress(
          Math.max(10, Math.round((done / Math.max(values.length, 1)) * 100)),
        );
        if (
          values.length > 0 &&
          done === values.length &&
          !completedRef.current
        ) {
          completedRef.current = true;
          window.setTimeout(onComplete, 650);
        }
      } catch {
        /* transient polling errors are retried */
      }
    };
    poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [productId, onComplete]);
  return (
    <div className={CONTAINER}>
      <div className={PANEL}>
        <div className={SPINNER} />
        <h2 className={TITLE}>商品情報を最適化しています</h2>
        <p className={COPY}>
          URLを読み取り、バナー制作に必要な情報を整理しています。
        </p>
        <div className={PROGRESS}>
          <div className={FILL} style={{ width: `${progress}%` }} />
        </div>
        <p className={COPY}>
          {progress}% ・{' '}
          {labels[Math.min(Math.floor(progress / 26), labels.length - 1)]}
          を確認中
        </p>
        <button className={SKIP} type="button" onClick={onSkip}>
          スキップ
        </button>
      </div>
      <div>
        <div className={VIDEO} aria-label="Taskyの使い方動画（準備中）">
          <div className={PLAY}>▶</div>
        </div>
        <p className={VIDEO_LABEL}>動画で見る：Taskyの使い方</p>
        <p className={VIDEO_NOTE}>動画は準備中です。後からこちらに追加されます。</p>
      </div>
    </div>
  );
};
export default SignupResearchStep;
